import { Box, Button, Grid2, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const Step1 = ({
  sra,
  setSra,
  setStep,
  storedUser,
  fileNames,
  setFileNames,
}) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const [plants, setPlants] = useState([]);
  const orgId = JSON.parse(localStorage.getItem("org"));
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    const getVendors = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/vendor-list/org?orgId=${orgId}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        setVendors(res.data);
      } catch (error) {
        console.log(error.response?.data?.Exception || "Access denied");
      }
    };

    getVendors();
  }, []);

  const handleVendorChange = (e) => {
    const selectedCode = e.target.value;

    const selectedVendor = vendors.find(
      (vendor) => vendor.code === selectedCode,
    );

    if (selectedVendor) {
      setSra((prev) => ({
        ...prev,
        vendorCode: selectedVendor.code || "",
        vendorName: selectedVendor.name || "",
        vendorDetails: selectedVendor.details || "",
        vendorEmail: selectedVendor.email || "",
        gst: selectedVendor.gst || "",
        pan: selectedVendor.pan || "",
      }));
    } else {
      setSra((prev) => ({
        ...prev,
        vendorCode: "",
        vendorName: "",
        gst: "",
        pan: "",
      }));
    }

    setErrors((prev) => ({
      ...prev,
      vendorCode: "",
      vendorName: "",
      gst: "",
      pan: "",
    }));
  };

  useEffect(() => {
    const getPlants = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/plant?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setPlants(res.data);
      } catch (error) {
        console.log(error.response.data.Exception || "Access denied");
      }
    };
    getPlants();
  }, []);

  const handleChange = (e) => {
    setSra({ ...sra, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  useEffect(() => {
    const total =
      Number(sra?.planningAmount || 0) +
      Number(sra?.tax || 0) +
      Number(sra?.other || 0);

    setSra((prev) => ({ ...prev, amount: total }));
  }, [sra?.planningAmount, sra?.tax, sra?.other]);

  const validateForm = () => {
    const newErrors = {};
    const data = sra || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    if (isEmpty(data.title)) {
      newErrors.title = "Title is required";
    }

    if (isEmpty(data.serviceName)) {
      newErrors.serviceName = "Service Name is required";
    }

    if (isEmpty(data.dept)) {
      newErrors.dept = "Requesting Department is required";
    }

    if (isEmpty(data.gl)) {
      newErrors.gl = "GL is required";
    }

    if (isEmpty(data.costCenter)) {
      newErrors.costCenter = "Cost Center is required";
    }

    if (!data.startDate) {
      newErrors.startDate = "Start Date is required";
    }

    if (!data.endDate) {
      newErrors.endDate = "End Date is required";
    } else if (
      data.startDate &&
      new Date(data.endDate) < new Date(data.startDate)
    ) {
      newErrors.endDate = "End Date cannot be before Start Date";
    }

    if (isEmpty(data.planningAmount)) {
      newErrors.planningAmount = "Basic Amount is required";
    } else if (isNaN(data.planningAmount) || Number(data.planningAmount) <= 0) {
      newErrors.planningAmount = "Enter a valid Basic Amount";
    }

    if (isEmpty(data.amount)) {
      newErrors.amount = "Total Amount is required";
    } else if (isNaN(data.amount) || Number(data.amount) <= 0) {
      newErrors.amount = "Enter a valid Total Amount";
    }

    if (isEmpty(data.serviceNeed)) {
      newErrors.serviceNeed = "Service Justification is required";
    } else if (data.serviceNeed.length > 250) {
      newErrors.serviceNeed =
        "Service Justification should be up to 250 characters";
    }

    if (isEmpty(data.paymentTerms)) {
      newErrors.paymentTerms = "Payment Terms are required";
    } else if (data.paymentTerms.length > 250) {
      newErrors.paymentTerms = "Payment Terms should be up to 250 characters";
    }

    if (isEmpty(data.otherTerms)) {
      newErrors.otherTerms = "Other Terms are required";
    } else if (data.otherTerms.length > 250) {
      newErrors.otherTerms = "Other Terms should be up to 250 characters";
    }

    if (isEmpty(data.plantDetails)) {
      newErrors.plantDetails = "Plant Details are required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    if (!isEmpty(data.vendorEmail) && !emailRegex.test(data.vendorEmail)) {
      newErrors.vendorEmail = "Enter a valid Vendor Email";
    }

    if (!isEmpty(data.contactEmail) && !emailRegex.test(data.contactEmail)) {
      newErrors.contactEmail = "Enter a valid Contact Email";
    }

    if (!isEmpty(data.pan) && !panRegex.test(data.pan.toUpperCase())) {
      newErrors.pan = "Enter a valid PAN (e.g., ABCDE1234F)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <h2>Service Order Approval Form</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "20%" }}></span>
      </div>
      <div className="dashboard-container">
        <Typography className="form-title">Service Details</Typography>

        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="title">
              Title <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.title && "input-error"}`}
              type="text"
              name="title"
              id="title"
              onChange={handleChange}
              value={sra?.title || ""}
            />
            {errors.title && <p className="error">{errors.title}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="serviceName">
              Service Name <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.serviceName && "input-error"}`}
              type="text"
              name="serviceName"
              id="serviceName"
              onChange={handleChange}
              value={sra?.serviceName || ""}
            />
            {errors.serviceName && (
              <p className="error">{errors.serviceName}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="dept">
              Requesting Department <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.dept && "input-error"}`}
              type="text"
              name="dept"
              id="dept"
              onChange={handleChange}
              value={sra?.dept || ""}
            />
            {errors.dept && <p className="error">{errors.dept}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="plantDetails">
              Plant Details <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <select
              name="plantDetails"
              id="plantDetails"
              className={`${errors.plantDetails && "input-error"}`}
              onChange={handleChange}
              value={sra?.plantDetails || ""}
            >
              <option value="">--Choose--</option>
              {plants?.map((plant) => (
                <option value={plant.plantId} key={plant.plantId}>
                  {plant.name}
                </option>
              ))}
            </select>
            {errors.plantDetails && (
              <p className="error">{errors.plantDetails}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="startDate">
              Start Date <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.startDate && "input-error"}`}
              type="date"
              name="startDate"
              id="startDate"
              onChange={handleChange}
              value={
                sra?.startDate
                  ? new Date(sra.startDate).toISOString().split("T")[0]
                  : ""
              }
            />
            {errors.startDate && <p className="error">{errors.startDate}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="endDate">
              End Date <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.endDate && "input-error"}`}
              type="date"
              name="endDate"
              id="endDate"
              onChange={handleChange}
              value={
                sra?.endDate
                  ? new Date(sra.endDate).toISOString().split("T")[0]
                  : ""
              }
            />
            {errors.endDate && <p className="error">{errors.endDate}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="planningAmount">
              Basic Amount <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.planningAmount && "input-error"}`}
              type="number"
              name="planningAmount"
              id="planningAmount"
              onChange={handleChange}
              value={sra?.planningAmount || ""}
            />
            {errors.planningAmount && (
              <p className="error">{errors.planningAmount}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="tax">Tax</label>
            <input
              className={`${errors.tax && "input-error"}`}
              type="number"
              step="0.01"
              name="tax"
              id="tax"
              onChange={handleChange}
              value={sra?.tax || ""}
            />
            {errors.tax && <p className="error">{errors.tax}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="other">Others</label>
            <input
              className={`${errors.other && "input-error"}`}
              type="number"
              step="0.01"
              name="other"
              id="other"
              onChange={handleChange}
              value={sra?.other || ""}
            />
            {errors.other && <p className="error">{errors.other}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="taxValue">Taxable Value</label>
            <input
              className={`${errors.taxValue && "input-error"}`}
              type="number"
              step="0.01"
              name="taxValue"
              id="taxValue"
              onChange={handleChange}
              value={sra?.taxValue || ""}
            />
            {errors.taxValue && <p className="error">{errors.taxValue}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="amount">
              Total Amount <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.amount && "input-error"}`}
              type="number"
              step="0.01"
              name="amount"
              id="amount"
              value={sra?.amount || ""}
              readOnly
            />
            {errors.amount && <p className="error">{errors.amount}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="vendorCode">Vendor SAP Code</label>
            <select
              name="vendorCode"
              id="vendorCode"
              onChange={handleVendorChange}
              value={sra?.vendorCode || ""}
              className={`${errors.code && "input-error"}`}
            >
              <option value="">---Select---</option>
              {vendors?.map((vendor) => (
                <option key={vendor.code} value={vendor.code}>
                  {vendor.code}
                </option>
              ))}
            </select>
            {errors.vendorCode && <p className="error">{errors.vendorCode}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="vendorName">Vendor Name</label>
            <input
              className={`${errors.vendorName && "input-error"}`}
              type="text"
              name="vendorName"
              id="vendorName"
              onChange={handleChange}
              value={sra?.vendorName || ""}
            />
            {errors.vendorName && <p className="error">{errors.vendorName}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="vendorDetails">Vendor Contact Details</label>
            <input
              className={`${errors.vendorDetails && "input-error"}`}
              type="text"
              name="vendorDetails"
              id="vendorDetails"
              onChange={handleChange}
              value={sra?.vendorDetails || ""}
            />
            {errors.vendorDetails && (
              <p className="error">{errors.vendorDetails}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="vendorEmail">Vendor Email</label>
            <input
              className={`${errors.vendorEmail && "input-error"}`}
              type="text"
              name="vendorEmail"
              id="vendorEmail"
              onChange={handleChange}
              value={sra?.vendorEmail || ""}
            />
            {errors.vendorEmail && (
              <p className="error">{errors.vendorEmail}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="gst">Vendor GST</label>
            <input
              className={`${errors.gst && "input-error"}`}
              type="text"
              name="gst"
              id="gst"
              onChange={handleChange}
              value={sra?.gst || ""}
            />
            {errors.gst && <p className="error">{errors.gst}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="pan">Vendor PAN</label>
            <input
              className={`${errors.pan && "input-error"}`}
              type="text"
              name="pan"
              id="pan"
              onChange={handleChange}
              value={sra?.pan || ""}
            />
            {errors.pan && <p className="error">{errors.pan}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="contactName">Contact Person Name</label>
            <input
              className={`${errors.contactName && "input-error"}`}
              type="text"
              name="contactName"
              id="contactName"
              onChange={handleChange}
              value={sra?.contactName || ""}
            />
            {errors.contactName && (
              <p className="error">{errors.contactName}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="contactDetails">Contact Person Details</label>
            <input
              className={`${errors.contactDetails && "input-error"}`}
              type="text"
              name="contactDetails"
              id="contactDetails"
              onChange={handleChange}
              value={sra?.contactDetails || ""}
            />
            {errors.contactDetails && (
              <p className="error">{errors.contactDetails}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="contactEmail">Contact Person Email</label>
            <input
              className={`${errors.contactEmail && "input-error"}`}
              type="text"
              name="contactEmail"
              id="contactEmail"
              onChange={handleChange}
              value={sra?.contactEmail || ""}
            />
            {errors.contactEmail && (
              <p className="error">{errors.contactEmail}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="gl">
              GL <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.gl && "input-error"}`}
              type="text"
              name="gl"
              id="gl"
              onChange={handleChange}
              value={sra?.gl || ""}
            />
            {errors.gl && <p className="error">{errors.gl}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="costCenter">
              Cost Center <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.costCenter && "input-error"}`}
              type="text"
              name="costCenter"
              id="costCenter"
              onChange={handleChange}
              value={sra?.costCenter || ""}
            />
            {errors.costCenter && <p className="error">{errors.costCenter}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="serviceNeed">
              Service Justification <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <textarea
              className={`${errors.serviceNeed && "input-error"}`}
              type="text"
              name="serviceNeed"
              id="serviceNeed"
              onChange={handleChange}
              value={sra?.serviceNeed || ""}
              rows={4}
            />
            {errors.serviceNeed && (
              <p className="error">{errors.serviceNeed}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="currentStatus">Current Status</label>
            <input
              className={`${errors.currentStatus && "input-error"}`}
              type="text"
              name="currentStatus"
              id="currentStatus"
              onChange={handleChange}
              value={sra?.currentStatus || ""}
            />
            {errors.currentStatus && (
              <p className="error">{errors.currentStatus}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="paymentTerms">
              Payment Terms <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <textarea
              className={`${errors.paymentTerms && "input-error"}`}
              type="text"
              name="paymentTerms"
              id="paymentTerms"
              onChange={handleChange}
              value={sra?.paymentTerms || ""}
              rows={2}
            />
            {errors.paymentTerms && (
              <p className="error">{errors.paymentTerms}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="otherTerms">
              Other Terms <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <textarea
              className={`${errors.otherTerms && "input-error"}`}
              type="text"
              name="otherTerms"
              id="otherTerms"
              onChange={handleChange}
              value={sra?.otherTerms || ""}
              rows={4}
            />
            {errors.otherTerms && <p className="error">{errors.otherTerms}</p>}
          </Grid2>
        </Grid2>
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={handleUpdate}
          disabled={btnLoading}
        >
          {btnLoading ? "Submitting..." : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step1;
